import { useState, type ReactNode } from "react";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Button } from "@/components/ui/Button";
import { Icon } from "@/components/ui/Icon";
import { Breadcrumb } from "@/components/ui/Breadcrumb";
import { BoardListRow } from "@/components/board/BoardListRow";
import { WorkspaceMenu } from "@/components/board/WorkspaceMenu";
import { WorkspaceBadge } from "@/components/ui/WorkspaceBadge";
import { CreateBoardDialog } from "@/components/dialogs/CreateBoardDialog";
import { ImportMondayDialog } from "@/components/dialogs/ImportMondayDialog";
import { usePinnedBoards } from "@/hooks/usePinnedBoards";
import { useReorderBoards } from "@/hooks/useReorderBoards";
import { api } from "@/lib/api";
import { queryKeys } from "@/lib/queryClient";
import { compareBoardOrder, reindexPositions } from "@/lib/boardOrder";
import { resolveWorkspaceColor, workspaceColor } from "@/lib/colors";
import { useBreakpoint } from "@/hooks/useBreakpoint";
import { t } from "@/lib/strings";
import type { Board, UUID } from "@/lib/types";

export function WorkspacePage() {
  const { workspaceId } = useParams({ strict: false }) as { workspaceId: string };
  const navigate = useNavigate();
  const isDesktop = useBreakpoint("md");
  const [createBoardOpen, setCreateBoardOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [filter, setFilter] = useState<"all" | "mine" | "pinned" | "archived">("all");

  const wsListQuery = useQuery({
    queryKey: queryKeys.workspaces,
    queryFn: api.listWorkspaces,
  });
  const boardsQuery = useQuery({
    queryKey: queryKeys.boards(workspaceId),
    queryFn: () => api.listBoards(workspaceId),
  });
  const pinned = usePinnedBoards();
  const pinnedIds = new Set((pinned.data ?? []).map((b) => b.id));

  const ws = wsListQuery.data?.find((w) => w.id === workspaceId);
  const allBoards: Board[] = boardsQuery.data ?? [];
  // F2: archived boards are hidden everywhere except the "Archiviert" filter.
  const boards: Board[] = (
    filter === "archived"
      ? allBoards.filter((b) => b.archived_at)
      : (() => {
          const active = allBoards.filter((b) => !b.archived_at);
          return filter === "pinned" ? active.filter((b) => pinnedIds.has(b.id)) : active;
        })()
  )
    .slice()
    .sort(compareBoardOrder); // F1: custom per-user order, name fallback

  // F1: drag-reorder is offered in the default "all" view (the full board list);
  // pinned/archived are subsets where a global reorder would be confusing.
  const reorder = useReorderBoards();
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));
  const dragEnabled = filter === "all" && boards.length > 1;

  function onBoardDragEnd(e: DragEndEvent) {
    const activeId = String(e.active.id);
    const overId = e.over?.id ? String(e.over.id) : null;
    if (!overId || activeId === overId) return;
    const from = boards.findIndex((b) => b.id === activeId);
    const to = boards.findIndex((b) => b.id === overId);
    if (from < 0 || to < 0) return;
    const next = arrayMove(boards, from, to);
    reorder.mutate({
      workspaceId: workspaceId as UUID,
      orders: reindexPositions(next) as { board_id: UUID; position: number }[],
    });
  }

  // wsListQuery is the source of truth for membership. If we have a list and
  // this workspace isn't in it, the user isn't a member (or it doesn't exist).
  if (wsListQuery.isSuccess && !ws) {
    return <NotFoundPanel />;
  }

  const wsColor = ws ? resolveWorkspaceColor(ws) : workspaceColor(workspaceId);
  const wsName = ws?.name ?? t.loading;

  return (
    <div
      style={{
        flex: 1,
        overflow: "auto",
        padding: isDesktop ? "28px 36px 80px" : "16px 12px 80px",
        background: "var(--bg)",
      }}
    >
      <div style={{ maxWidth: 1180, margin: "0 auto" }}>
        <div style={{ marginBottom: 10 }}>
          <Breadcrumb items={[{ label: t.navHome, to: "/" }, { label: wsName }]} />
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 22 }}>
          <WorkspaceBadge name={wsName} icon={ws?.icon} color={wsColor} size={48} />
          <div style={{ flex: 1, display: "flex", alignItems: "center", gap: 6, minWidth: 0 }}>
            <h1
              style={{
                margin: 0,
                fontSize: 24,
                fontWeight: 600,
                color: "var(--ink-1)",
                letterSpacing: "-0.015em",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {wsName}
            </h1>
            {/* The Persönlich workspace is a system bucket — no rename/delete/
                share/members/import. */}
            {ws && !ws.is_personal ? (
              <WorkspaceMenu
                workspaceId={ws.id}
                workspaceName={ws.name}
                workspaceColor={ws.color}
                workspaceIcon={ws.icon}
                workspaceCompany={ws.company}
                teamsConfigured={ws.teams_configured}
                boardCount={allBoards.length}
              />
            ) : null}
          </div>
          <p style={{ margin: 0, fontSize: 13, color: "var(--ink-3)", marginRight: 8 }}>
            {boards.length} {boards.length === 1 ? "Board" : t.boardCount}
          </p>
          {ws && !ws.is_personal ? (
            <Button variant="ghost" size="md" onClick={() => setImportOpen(true)} disabled={!ws}>
              {t.mondayImport}
            </Button>
          ) : null}
          <Button
            variant="primary"
            size="md"
            icon="plus"
            onClick={() => setCreateBoardOpen(true)}
            disabled={!ws}
          >
            {t.newBoard}
          </Button>
        </div>

        {ws?.is_personal ? (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              marginBottom: 14,
              padding: "8px 12px",
              borderRadius: 8,
              background: "var(--surface-soft)",
              border: "1px solid var(--hairline)",
              fontSize: 12.5,
              color: "var(--ink-3)",
            }}
          >
            <Icon name="user-round-key" size={15} color="var(--ink-3)" />
            {t.personalWorkspaceHint}
          </div>
        ) : null}

        <div style={{ display: "flex", alignItems: "center", gap: 4, marginBottom: 14 }}>
          {(
            [
              { id: "all", label: t.filtersAll },
              { id: "mine", label: t.filtersMine },
              { id: "pinned", label: t.filtersPinned },
              { id: "archived", label: t.filtersArchived },
            ] as const
          ).map((f) => (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              style={{
                padding: "6px 12px",
                fontSize: 12.5,
                borderRadius: 7,
                border: "none",
                background: filter === f.id ? "var(--surface-elev)" : "transparent",
                color: filter === f.id ? "var(--ink-1)" : "var(--ink-3)",
                fontWeight: filter === f.id ? 500 : 400,
                cursor: "pointer",
                fontFamily: "Inter Variable, Inter, system-ui, sans-serif",
              }}
            >
              {f.label}
            </button>
          ))}
        </div>

        {boardsQuery.isLoading ? (
          <SkeletonGrid />
        ) : boards.length === 0 ? (
          <EmptyState
            title={t.emptyBoardsTitle}
            hint={t.emptyBoardsHint}
            cta={
              <Button
                variant="primary"
                size="md"
                icon="plus"
                onClick={() => setCreateBoardOpen(true)}
                disabled={!ws}
              >
                {t.newBoard}
              </Button>
            }
          />
        ) : dragEnabled ? (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={onBoardDragEnd}
          >
            <SortableContext items={boards.map((b) => b.id)} strategy={verticalListSortingStrategy}>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {boards.map((b) => (
                  <SortableBoardRow
                    key={b.id}
                    board={b}
                    onClick={() => navigate({ to: "/b/$boardId", params: { boardId: b.id } })}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {boards.map((b) => (
              <BoardListRow
                key={b.id}
                board={b}
                onClick={() => navigate({ to: "/b/$boardId", params: { boardId: b.id } })}
              />
            ))}
          </div>
        )}
      </div>

      {ws ? (
        <CreateBoardDialog
          open={createBoardOpen}
          onOpenChange={setCreateBoardOpen}
          workspaceId={ws.id}
          isPersonal={ws.is_personal}
        />
      ) : null}

      {ws ? (
        <ImportMondayDialog open={importOpen} onOpenChange={setImportOpen} workspaceId={ws.id} />
      ) : null}
    </div>
  );
}

// F1: a board row wrapped for drag-reorder. Whole-row drag with an 8px
// activation distance, so a plain click still opens the board (matches the
// table-row pattern); the menu button stops its own propagation.
function SortableBoardRow({ board, onClick }: { board: Board; onClick: () => void }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: board.id,
  });
  return (
    <div
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        cursor: "grab",
      }}
    >
      <BoardListRow board={board} onClick={onClick} />
    </div>
  );
}

function NotFoundPanel() {
  return (
    <div
      style={{
        flex: 1,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "var(--bg)",
        padding: 48,
      }}
    >
      <div style={{ textAlign: "center", maxWidth: 440 }}>
        <h1
          style={{
            margin: 0,
            fontSize: 22,
            fontWeight: 600,
            color: "var(--ink-1)",
            letterSpacing: "-0.01em",
          }}
        >
          {t.workspaceNotFoundTitle}
        </h1>
        <p
          style={{ fontSize: 13.5, color: "var(--ink-3)", margin: "10px 0 22px", lineHeight: 1.5 }}
        >
          {t.workspaceNotFoundHint}
        </p>
        <Link to="/" style={{ textDecoration: "none" }}>
          <Button variant="primary" size="md" icon="chevRight">
            {t.backToDashboard}
          </Button>
        </Link>
      </div>
    </div>
  );
}

function EmptyState({ title, hint, cta }: { title: string; hint: string; cta?: ReactNode }) {
  return (
    <div
      style={{
        background: "var(--surface)",
        borderRadius: 10,
        border: "1px dashed var(--hairline-strong)",
        padding: "40px 24px",
        textAlign: "center",
      }}
    >
      <div style={{ fontSize: 15, fontWeight: 500, color: "var(--ink-1)", marginBottom: 6 }}>
        {title}
      </div>
      <div style={{ fontSize: 13, color: "var(--ink-3)", marginBottom: cta ? 16 : 0 }}>{hint}</div>
      {cta}
    </div>
  );
}

function SkeletonGrid() {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
        gap: 14,
      }}
    >
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          style={{
            height: 130,
            background: "var(--surface)",
            border: "1px solid var(--hairline)",
            borderRadius: 10,
            opacity: 0.55,
          }}
        />
      ))}
    </div>
  );
}
